import { computed, effect, inject } from '@angular/core';
import { UserSettingService } from '@thiememeulenhoff/ng-api-gateway';
import { UserSetting } from '@thiememeulenhoff/types';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
} from '@ngrx/signals';
import {
  entityConfig,
  removeEntity,
  setAllEntities,
  upsertEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { firstValueFrom } from 'rxjs';
import {
  RequestStatus,
  withRequestStatus,
} from '../_utils/request-status.feature';

const userSettingEntityConfig = entityConfig({
  entity: {} as UserSetting,
  selectId: (setting: UserSetting) => setting.key,
});

export const UserSettingStore = signalStore(
  { providedIn: 'root' },
  withEntities(userSettingEntityConfig),
  withRequestStatus(),
  withComputed(({ entities }) => ({
    userSettings: computed(() => entities()),
  })),
  withMethods((store, userSettingService = inject(UserSettingService)) => {
    const fetchUserSettings = async (): Promise<Array<UserSetting>> => {
      patchState(store, { requestStatus: 'loading' as RequestStatus });
      try {
        const userSettings = await firstValueFrom(
          userSettingService.fetchUserSettings(),
        );
        patchState(
          store,
          setAllEntities(userSettings, userSettingEntityConfig),
          {
            requestStatus: 'success' as RequestStatus,
          },
        );
        return userSettings;
      } catch (error) {
        patchState(store, {
          requestStatus: {
            error:
              error instanceof Error
                ? error.message
                : 'Failed to fetch user settings',
          } as RequestStatus,
        });
        throw error;
      }
    };

    const selectors = {
      userSettingByKey: (key: string) =>
        computed(() => store.entities().find((setting) => setting.key === key)),
      userSettingValue: (key: string) =>
        computed(
          () => store.entities().find((setting) => setting.key === key)?.value,
        ),
    };

    const entityMethods = {
      setUserSettings(userSettings: Array<UserSetting>): void {
        patchState(
          store,
          setAllEntities(userSettings, userSettingEntityConfig),
        );
      },
      upsertUserSetting(userSetting: UserSetting): void {
        patchState(store, upsertEntity(userSetting, userSettingEntityConfig));
      },
      removeUserSetting(key: string): void {
        patchState(store, removeEntity(key));
      },
    };

    const apiMethods = {
      fetchUserSettings,
      async setUserSetting(
        userSetting: UserSetting,
        shouldUpsertFirst = false,
        shouldReload = true,
        optimisticUpdateAlreadyApplied = false,
      ): Promise<Array<UserSetting> | undefined> {
        patchState(store, { requestStatus: 'loading' as RequestStatus });
        if (shouldUpsertFirst && !optimisticUpdateAlreadyApplied) {
          patchState(store, upsertEntity(userSetting, userSettingEntityConfig));
        }

        try {
          await firstValueFrom(
            userSettingService.createUserSetting(userSetting),
          );
          if (!shouldUpsertFirst) {
            patchState(
              store,
              upsertEntity(userSetting, userSettingEntityConfig),
            );
          }
        } catch (error) {
          patchState(store, {
            requestStatus: {
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to set user setting',
            } as RequestStatus,
          });
          throw error;
        }

        if (shouldReload) {
          return fetchUserSettings();
        }
        patchState(store, {
          requestStatus: 'success' as RequestStatus,
        });
        return undefined;
      },
      async deleteUserSetting(
        key: string,
        shouldReload = true,
      ): Promise<Array<UserSetting> | undefined> {
        patchState(store, { requestStatus: 'loading' as RequestStatus });
        patchState(store, removeEntity(key));

        try {
          await firstValueFrom(userSettingService.deleteUserSetting(key));
        } catch (error) {
          patchState(store, {
            requestStatus: {
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to delete user setting',
            } as RequestStatus,
          });
          throw error;
        }

        if (shouldReload) {
          return fetchUserSettings();
        }
        patchState(store, {
          requestStatus: 'success' as RequestStatus,
        });
        return undefined;
      },
      async getUserSetting(key: string): Promise<UserSetting | null> {
        patchState(store, { requestStatus: 'loading' as RequestStatus });
        try {
          const userSetting = await firstValueFrom(
            userSettingService.getUserSetting(key),
          );
          if (userSetting) {
            patchState(
              store,
              upsertEntity(userSetting, userSettingEntityConfig),
            );
          }
          patchState(store, {
            requestStatus: 'success' as RequestStatus,
          });
          return userSetting;
        } catch (error) {
          patchState(store, {
            requestStatus: {
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to fetch user setting',
            } as RequestStatus,
          });
          throw error;
        }
      },
    };

    return {
      ...selectors,
      ...entityMethods,
      ...apiMethods,
    };
  }),
  withHooks({
    onInit(store) {
      const darkMode = store.userSettingValue('dark-mode');

      effect(() => {
        document.documentElement.setAttribute(
          'data-theme',
          darkMode() === 'dark' ? 'dark' : 'light',
        );
      });
    },
  }),
);
